import { useCallback, useMemo, useState } from "react"
import { Alert, Autocomplete, Grid, MenuItem, TextField } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef } from "material-react-table"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { IndustryCodeChoice } from "../SupplierAdminPOSTypes"
import data from "../data/adyenIndustryCodes.json"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { createIndustryCode, POSQuery } from "../SupplierAdminPOSApi"
import { useSnackbar } from "notistack"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"
import { ApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useColumnFactory } from "../../../../../../core/api/useTableColumns"

const useStyles = makeStyles()((theme) => ({
    contentGrid: {
        padding: "0 2rem 2rem 2rem",
    },
    button: {
        minWidth: "7.5rem",
    },
    tableContainer: {
        maxHeight: "100%",
    },
    tableHeadRow: {
        background: theme.palette.tailwind.slate[50],
    },
    tableHeadCell: {
        border: "none",
        color: theme.palette.tailwind.slate[500],
    },
    tableBodyCell: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
}))

function compareCodes(x: IndustryCodeChoice, y: IndustryCodeChoice) {
    const cmp = x.code.length - y.code.length
    if (cmp !== 0) return cmp
    return x.code.localeCompare(y.code)
}

function transformIndustryCodes(codes: typeof data.industryCodes): IndustryCodeChoice[] {
    const merged = codes.noApprovalNeeded
        .map((code) => ({
            code: code["Industry code"],
            description: code.Description,
            requiresApproval: false,
        }))
        .concat(
            codes.requiresApproval.map((code) => ({
                code: code["Industry code"],
                description: code.Description,
                requiresApproval: true,
            })),
        )

    return merged.sort(compareCodes)
}

const industryCodes = transformIndustryCodes(data.industryCodes)

type Props = Readonly<{
    agreementId: number
    open: boolean
    onClose: () => void
    onSave: (pickedCodes: IndustryCodeChoice[]) => void
}>

export function AddIndustryCodeDialog({ agreementId, open, onClose, onSave }: Props) {
    const { classes } = useStyles()

    const [selected, setSelected] = useState<IndustryCodeChoice | null>(null)
    const [options, setOptions] = useState<IndustryCodeChoice[]>(industryCodes)
    const [picks, setPicks] = useState<IndustryCodeChoice[]>([])

    const { enqueueSnackbar } = useSnackbar()
    const { supplierId } = useCurrentSupplier()

    const columnFactory = useColumnFactory<IndustryCodeChoice>()

    const columns = useMemo<MRT_ColumnDef<IndustryCodeChoice>[]>(
        () => [
            columnFactory.idColumn({
                header: "Industry code",
                accessorKey: "code",
            }),
            columnFactory.nameColumn({
                header: "Description",
                accessorKey: "description",
            }),
            columnFactory.typeColumn({
                header: "",
                accessorKey: "requiresApproval",
                Cell: ({ cell }) => {
                    if (cell.getValue<boolean>()) {
                        return (
                            <StandardChip variant="red" placement="table">
                                Approval required
                            </StandardChip>
                        )
                    }
                    return null
                },
            }),
        ],

        [columnFactory],
    )

    const { mutateAsync: callCreateIndustryCode } = useApiMutation(createIndustryCode, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [POSQuery.INDUSTRY_CODES_DETAILS] })
            await context.queryClient.invalidateQueries({ queryKey: [POSQuery.AGREEMENTS_DETAILS] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
            onSave(picks)
        },
    })

    const handleAddCode = useCallback(() => {
        if (!selected) return
        setPicks((prev) => [...prev, selected].sort(compareCodes))
        setOptions((prev) => prev.filter((option) => option.code !== selected.code))
        setSelected(null)
    }, [selected, setSelected, setPicks, setOptions, compareCodes])

    const handleRemoveCode = useCallback(
        (code: IndustryCodeChoice) => {
            setPicks((prev) => prev.filter((pick) => pick.code !== code.code))
            setOptions((prev) => [...prev, code].sort(compareCodes))
        },
        [setPicks, setOptions, compareCodes],
    )

    const handleSave = useCallback(async () => {
        for (const pick of picks) {
            await callCreateIndustryCode({
                agreementId,
                industryCode: {
                    supplier: {
                        id: supplierId,
                    },
                    code: pick.code,
                },
            })
        }
    }, [picks, onSave])

    return (
        <StandardDialog dialogTitle="Add industry code" maxWidthProp="md" scroll="body" open={open} onClose={onClose}>
            <Grid container direction="column" wrap="nowrap" gap="2rem" className={classes.contentGrid}>
                <LocalizedStrict id="supplier-acquiring-industry-codes-alert-title">
                    <Alert severity="info">
                        Adding new industry codes might change your payment acquiring agreement status to pending if the
                        acquirer needs to verify.
                    </Alert>
                </LocalizedStrict>
                <Grid container wrap="nowrap" justifyContent="space-between" gap="1.5rem">
                    <Grid item flexGrow={1}>
                        <Autocomplete
                            options={options}
                            getOptionLabel={(option) => `${option.code} ${option.description}`}
                            isOptionEqualToValue={(option, value) => option.code === value.code}
                            value={selected}
                            onChange={(_event, value) => setSelected(value)}
                            renderInput={(params) => (
                                <TextField {...params} label="Code" size="small" InputProps={params.InputProps} />
                            )}
                        />
                    </Grid>
                    <LocalizedStrict id="supplier-acquiring-industry-codes-button-add">
                        <StandardButton
                            className={classes.button}
                            onClick={handleAddCode}
                            fullWidth={false}
                            defaultBorderRadius
                            size="small"
                            color="blue"
                        >
                            Add
                        </StandardButton>
                    </LocalizedStrict>
                </Grid>
                {picks.length > 0 && (
                    <ApiQueryTable
                        columns={columns}
                        data={picks}
                        enablePagination={false}
                        enableTopToolbar={false}
                        enableBottomToolbar={false}
                        isLoading={false}
                        isError={false}
                        isFetching={false}
                        enableColumnActions={false}
                        muiTablePaperProps={{ elevation: 0 }}
                        muiTableContainerProps={{ className: classes.tableContainer }}
                        muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                        muiTableHeadCellProps={{ className: classes.tableHeadCell }}
                        muiTableBodyCellProps={{ className: classes.tableBodyCell }}
                        renderRowActionMenuItems={({ row, closeMenu }) => [
                            <MenuItem
                                onClick={() => {
                                    handleRemoveCode(row.original)
                                    closeMenu()
                                }}
                                key={`delete-${row.original.code}`}
                            >
                                <LocalizedStrict id="supplier-acquiring-industry-codes-table-remove-action">
                                    <span>Remove</span>
                                </LocalizedStrict>
                            </MenuItem>,
                        ]}
                    />
                )}
                <Grid item container justifyContent="center">
                    <LocalizedStrict id="supplier-acquiring-industry-codes-button-save">
                        <StandardButton
                            disabled={picks.length === 0}
                            fullWidth={false}
                            onClick={handleSave}
                            className={classes.button}
                        >
                            Save
                        </StandardButton>
                    </LocalizedStrict>
                </Grid>
            </Grid>
        </StandardDialog>
    )
}
